import React from "react";
import { useFormContext, Controller } from "react-hook-form";
import { FormattedMessage } from "react-intl";

import { classNames } from "helpers/commonHelpers";
import { useRequestFormSaveOrNull } from "@RequestFormV2/context/save/RequestFormSaveContext";
import "./Textarea.scss";

interface TextareaProps {
  /**
   * Label text associated with the textarea element.
   */
  label?: string;
  /**
   * Additional text rendered under the textarea element.
   */
  hintText?: string;
  /**
   * Placeholder text displayed when the textarea is empty.
   */
  placeholder?: string;
  /**
   * Indicates whether the textarea is required.
   */
  required?: boolean;
  /**
   * Disables the textarea if true.
   */
  disabled?: boolean;
  /**
   * Indicates if the textarea is read-only.
   */
  readOnly?: boolean;
  /**
   * Error message displayed below the textarea when not using React-hook-form.
   */
  errorMessage?: string;
  /**
   * Sets the width of the textarea. Can be a string (e.g. "100%") or number (px).
   */
  width?: string | number;
  /**
   * Number of visible text lines. Default is 4.
   */
  rows?: number;
  /**
   * Controls resize behaviour. Default is "vertical".
   */
  resize?: "none" | "vertical" | "horizontal" | "both";
  /**
   * Necessary when using with React-hook-form.
   */
  name?: string;
  /**
   * Object specifying validation rules for React-hook-form.
   */
  registerOptions?: object;
  /**
   * Default value for the textarea when using React-hook-form.
   */
  defaultValue?: string;
  /**
   * If true, the field will be unregistered when unmounted.
   */
  shouldUnregister?: boolean;
  /**
   * Value when not using React-hook-form.
   */
  value?: string;
  /**
   * Callback triggered when the value changes (uncontrolled usage).
   */
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  /**
   * ID of the textarea element.
   */
  id?: string;
  /**
   * data-testid for the textarea element.
   */
  testId?: string;
  /**
   * When true (default), saves the field on blur if RequestFormSaveProvider is present.
   */
  saveOnBlur?: boolean;
}

const Textarea = function ({
  id,
  label,
  hintText,
  placeholder,
  required,
  disabled,
  readOnly,
  errorMessage,
  width,
  rows,
  resize,
  name,
  registerOptions,
  defaultValue,
  shouldUnregister,
  value,
  onChange,
  testId,
  saveOnBlur = true,
}: TextareaProps) {
  const resolvedTestId = testId || (name ? `textarea-${name.replace(/[.[\]]+/g, "-").replace(/-+$/g, "")}` : undefined);
  const formContext = useFormContext();
  const control = formContext ? formContext.control : null;
  const saveContext = useRequestFormSaveOrNull();

  const renderTextarea = (fieldProps: any = {}, error: any = null) => (
    <div className="textareaV2" style={{ width }}>
      {label && (
        <label className={classNames({ required })}>{label}</label>
      )}
      <div className={`textarea-container ${error ? "error" : ""} ${disabled ? "disabled" : ""}`}>
        <textarea
          id={id}
          rows={rows}
          style={{ resize }}
          data-testid={resolvedTestId}
          {...fieldProps}
          disabled={disabled}
          value={fieldProps.value !== undefined ? fieldProps.value : value}
          onChange={(e) => {
            if (fieldProps.onChange) fieldProps.onChange(e);
            if (onChange) onChange(e);
          }}
          onBlur={(e) => {
            if (fieldProps.onBlur) fieldProps.onBlur(e);
            if (saveOnBlur && name && saveContext) {
              saveContext.saveField(name, e.target.value).catch(() => undefined);
            }
          }}
          placeholder={placeholder}
          readOnly={readOnly}
        />
      </div>
      {error && (
        <div className="text-sm error-text text-error-primary">
          {error.message ? (
            error.message
          ) : (
            <FormattedMessage id="general.requiredField" defaultMessage="This is a required field" />
          )}
        </div>
      )}
      {hintText && !error && <div className="text-sm hint-text">{hintText}</div>}
    </div>
  );

  if (name && control) {
    return (
      <Controller
        name={name}
        control={control}
        shouldUnregister={shouldUnregister}
        rules={{ required, ...registerOptions }}
        defaultValue={defaultValue}
        render={({ field, fieldState }) => {
          const { error } = fieldState;
          return renderTextarea(field, error);
        }}
      />
    );
  }

  return renderTextarea({}, errorMessage && { message: errorMessage });
};

Textarea.defaultProps = {
  id: undefined,
  label: "",
  hintText: "",
  placeholder: "",
  required: false,
  disabled: false,
  readOnly: false,
  errorMessage: undefined,
  width: "100%",
  rows: 4,
  resize: "vertical",
  name: "",
  registerOptions: {},
  defaultValue: "",
  shouldUnregister: false,
  value: undefined,
  onChange: () => {},
};

export default Textarea;
